/**
 * AdminDataManagementPage — Iter29.
 *
 * Super-admin only. Lets the admin selectively wipe transactional data per
 * module after the app is live (post-testing cleanup), while preserving
 * users / roles / categories / configurations.
 *
 * Each module card shows:
 *   - module label + description
 *   - live row count (across all underlying collections)
 *   - a "Purge" button that opens the confirmation modal
 *
 * Confirmation modal requires:
 *   1. Typing "DELETE" in the confirm field
 *   2. Re-entering the admin's password
 * Both are sent to POST /api/admin/data-management/purge/{module_key}.
 *
 * After a successful purge, the row count refreshes automatically. The purge
 * itself is recorded in the audit log (except when purging the audit log).
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import {
  Database, ShieldAlert, Trash2, AlertTriangle, Lock, RefreshCw, Loader2, Download,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDataManagementPage = () => {
  const { user } = useAuth();
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [backingUp, setBackingUp] = useState(false);

  const downloadBackup = async () => {
    setBackingUp(true);
    try {
      const r = await axios.get(`${API_URL}/api/admin/backup/export`, { responseType: 'blob' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(r.data);
      a.download = `suubi_backup_${new Date().toISOString().slice(0, 10)}.zip`;
      a.click();
      toast.success('Full backup downloaded — store it somewhere safe. Repeat weekly for best protection.');
    } catch { toast.error('Backup export failed'); } finally { setBackingUp(false); }
  };
  const [target, setTarget] = useState(null);  // module entry being purged
  const [confirmText, setConfirmText] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const loadCatalog = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/api/admin/data-management/catalog`);
      setModules(res.data?.modules || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load data catalog');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCatalog();
  }, []);

  if (user?.role !== 'super_admin') {
    return (
      <div className="container max-w-3xl mx-auto py-12">
        <Alert variant="destructive">
          <Lock className="h-4 w-4" />
          <AlertTitle>Restricted</AlertTitle>
          <AlertDescription>
            Data Management is only accessible to the Super Admin.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  const closeDialog = () => {
    if (submitting) return;
    setTarget(null);
    setConfirmText('');
    setPassword('');
  };

  const submitPurge = async (e) => {
    e?.preventDefault?.();
    if (!target) return;
    if (confirmText !== 'DELETE') {
      toast.error('Type DELETE exactly to confirm');
      return;
    }
    if (!password) {
      toast.error('Re-enter your password');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/admin/data-management/purge/${target.key}`,
        { confirm: confirmText, password },
      );
      toast.success(`${res.data.label}: ${res.data.deleted_total} record(s) deleted`);
      setTarget(null);
      setConfirmText('');
      setPassword('');
      loadCatalog();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Purge failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="container max-w-6xl mx-auto py-8 space-y-6" data-testid="data-management-page">
      {/* Hero header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold flex items-center gap-2">
            <Database className="h-7 w-7 text-primary" /> Data Management
          </h1>
          <p className="text-muted-foreground mt-1 max-w-2xl">
            Selectively wipe transactional data per module after the app is live.
            Users, roles, categories, and configuration are <strong>never</strong> touched.
          </p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" onClick={downloadBackup} disabled={backingUp} data-testid="backup-export-btn">
            <Download className={`h-4 w-4 mr-1.5 ${backingUp ? 'animate-bounce' : ''}`} />
            {backingUp ? 'Preparing backup…' : 'Download Full Backup'}
          </Button>
          <Button variant="outline" size="sm" onClick={loadCatalog} disabled={loading} data-testid="data-mgmt-refresh">
            <RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh counts
          </Button>
        </div>
      </div>

      {/* Warning banner */}
      <Alert variant="destructive" className="border-2">
        <ShieldAlert className="h-5 w-5" />
        <AlertTitle>Destructive operation — no undo</AlertTitle>
        <AlertDescription>
          Purging a module hard-deletes <strong>every record</strong> in its collections.
          You must type <code className="bg-red-100/30 px-1 rounded">DELETE</code> + re-enter your password to confirm.
          Each purge is recorded in the audit log (except when purging the audit log itself).
        </AlertDescription>
      </Alert>

      {/* Module grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map((i) => <Skeleton key={i} className="h-44" />)}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {modules.map((m) => (
            <Card
              key={m.key}
              className="card-hover"
              data-testid={`purge-card-${m.key}`}
            >
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between gap-2">
                  <CardTitle className="text-base font-heading">{m.label}</CardTitle>
                  <Badge variant={m.total > 0 ? 'secondary' : 'outline'} data-testid={`purge-count-${m.key}`}>
                    {m.total} rec
                  </Badge>
                </div>
                <CardDescription className="text-xs leading-snug">
                  {m.description}
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-1 space-y-3">
                <div className="text-[11px] text-muted-foreground space-y-0.5">
                  {Object.entries(m.counts).map(([col, n]) => (
                    <div key={col} className="flex justify-between">
                      <code className="font-mono">{col}</code>
                      <span>{n}</span>
                    </div>
                  ))}
                </div>
                <Button
                  variant="destructive" size="sm" className="w-full"
                  disabled={m.total === 0}
                  onClick={() => setTarget(m)}
                  data-testid={`purge-btn-${m.key}`}
                >
                  <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                  {m.total === 0 ? 'Nothing to purge' : `Purge ${m.label}`}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Confirmation modal */}
      <Dialog open={!!target} onOpenChange={(v) => !v && closeDialog()}>
        <DialogContent className="max-w-md" data-testid="purge-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-5 w-5" /> Confirm purge — {target?.label}
            </DialogTitle>
            <DialogDescription>
              This will permanently delete <strong>{target?.total ?? 0}</strong> records across{' '}
              <code className="text-xs">{(target?.collections || []).join(', ')}</code>.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={submitPurge} className="space-y-3">
            <div>
              <Label htmlFor="confirm">Type <code>DELETE</code> to confirm</Label>
              <Input
                id="confirm"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="DELETE"
                autoComplete="off"
                data-testid="purge-confirm-input"
                disabled={submitting}
              />
            </div>
            <div>
              <Label htmlFor="purge-pw">Re-enter your password</Label>
              <Input
                id="purge-pw"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                data-testid="purge-password-input"
                disabled={submitting}
              />
            </div>

            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={closeDialog} disabled={submitting}>
                Cancel
              </Button>
              <Button
                type="submit" variant="destructive"
                disabled={submitting || confirmText !== 'DELETE' || !password}
                data-testid="purge-submit-btn"
              >
                {submitting && <Loader2 className="h-4 w-4 animate-spin mr-1.5" />}
                {submitting ? 'Purging…' : 'Purge permanently'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDataManagementPage;
